import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_THRESHOLD = 10;

function findScrollableAncestor(el: HTMLElement): HTMLElement | Window {
    let node = el.parentElement;
    while (node) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
        node = node.parentElement;
    }
    return window;
}

export function useScrollToBottom(deps: unknown[] = []) {
    const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
    const contentEndRef = useRef<HTMLDivElement>(null);
    const scrollParentRef = useRef<HTMLElement | Window | null>(null);

    const reset = useCallback(() => setHasScrolledToEnd(false), []);

    const scrollToBottom = useCallback(() => {
        const sp = scrollParentRef.current;
        if (sp && sp !== window) {
            (sp as HTMLElement).scrollTo({ top: (sp as HTMLElement).scrollHeight, behavior: 'smooth' });
        } else {
            contentEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        const sentinel = contentEndRef.current;
        if (!sentinel) return;

        const scrollParent = findScrollableAncestor(sentinel);
        scrollParentRef.current = scrollParent;

        const checkScrollEnd = () => {
            const isWindow = scrollParent === window;
            const nearBottom = isWindow
                ? window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - SCROLL_THRESHOLD
                : (scrollParent as HTMLElement).scrollTop + (scrollParent as HTMLElement).clientHeight >=
                  (scrollParent as HTMLElement).scrollHeight - SCROLL_THRESHOLD;
            if (nearBottom) setHasScrolledToEnd(true);
        };

        scrollParent.addEventListener('scroll', checkScrollEnd, { passive: true });
        checkScrollEnd();
        return () => scrollParent.removeEventListener('scroll', checkScrollEnd);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { contentEndRef, hasScrolledToEnd, scrollToBottom, reset };
}
